/**
 * ProjectTeamView — `/projects/$projectId/team`
 *
 * Tab "Equipo & RACI" del proyecto. **3 secciones, partidas por el TIER
 * de licencia del usuario** (OT-0008 §4.3), no por el rol funcional:
 *  1. Equipo operativo — tier `editor` (y `admin` que además trabaje).
 *     Produce documentos y puede ser responsable de uno. Aquí vive el
 *     "Líder activo" del proyecto (singleton `project.lead_member_id`)
 *     con su badge y el botón "Transferir liderazgo".
 *  2. Equipo extendido — tier `reviewer`. Lee el proyecto completo y
 *     **aprueba** cuando un flujo lo nombra, pero no produce documentos.
 *     Un "observador" no es un tipo aparte: es un revisor al que ningún
 *     flujo nombró como aprobador.
 *  3. Stakeholders del cliente — tier `external`. Onboarded (con
 *     user_id) o pendientes (solo external_email).
 *
 * El rol funcional (`project_members.role`) es etiqueta organizacional
 * y NO decide la sección: alguien con rol "Analista" y tier `reviewer`
 * no produce nada.
 *
 * CRUD completo: listar, añadir (3 diálogos: operacional / extendido /
 * stakeholder), editar role/RACI inline, quitar.
 *
 * Toast feedback en todas las mutations (verde éxito, rojo error).
 */

import { useMemo, useState } from 'react'
import { canJoinSection, canManageTeam, type TeamSection } from '../../lib/tiers'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ArrowDownUp,
  ArrowRightLeft,
  Bell,
  Crown,
  Pencil,
  Trash2,
  UserPlus,
} from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  listProjectMembers,
  updateProjectMember,
  type ProjectMember,
  type RaciRole,
} from '../../api/project_members'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { useProjectAccess } from '../../hooks/useProjectAccess'
import { useProjectRoles } from '../../hooks/useProjectRoles'
import { AddInternalMemberDialog } from '../../components/projects/team/AddInternalMemberDialog'
import { AddStakeholderDialog } from '../../components/projects/team/AddStakeholderDialog'
import { EditMemberRolesDialog } from '../../components/projects/team/EditMemberRolesDialog'
import { RemoveMemberDialog } from '../../components/projects/team/RemoveMemberDialog'
import {
  filterRolesByScope,
  roleLabel,
  type RoleOption,
} from '../../components/projects/team/roles'
import { TransferOwnershipDialog } from '../../components/projects/TransferOwnershipDialog'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { pushToast } from '../../stores/toastStore'
import { useProjectContext } from './ProjectShell'

export function ProjectTeamView() {
  const { t, i18n } = useTranslation(['projects', 'errors'])
  const { project } = useProjectContext()
  const { accessToken } = useAccessToken()
  const { me } = useMe()

  const [addOperationalOpen, setAddOperationalOpen] = useState(false)
  const [addExtendedOpen, setAddExtendedOpen] = useState(false)
  const [addStakeholderOpen, setAddStakeholderOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<ProjectMember | null>(
    null,
  )
  const [memberEditingRoles, setMemberEditingRoles] =
    useState<ProjectMember | null>(null)

  const membersQuery = useQuery({
    queryKey: ['projects', project.id, 'members', 'all'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectMembers(accessToken, project.id)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  // OT-0058 — roles que se pueden asignar en este proyecto: los de la
  // metodología de su ciclo, más los que piden sus flujos de aprobación
  // (aunque no sean responsables de ningún documento), más los que ya
  // tienen sus miembros. Lo calcula el backend contra el snapshot del
  // proyecto, que es contra lo que se resuelven las aprobaciones, y valida
  // las altas con la misma lista.
  const rolesQuery = useProjectRoles(project.id)
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data])

  // Equipo operativo y extendido ofrecen los mismos roles internos: la
  // sección sale de la licencia de la persona (OT-0008 §4.3), no del rol,
  // y un revisor puede ser, p. ej., el "Gerente comercial" que aprueba.
  const internalRoles = useMemo(
    () => filterRolesByScope(roles, 'internal', i18n.language),
    [roles, i18n.language],
  )
  const stakeholderRoles = useMemo(
    () => filterRolesByScope(roles, 'client_stakeholder', i18n.language),
    [roles, i18n.language],
  )

  const members = useMemo(() => membersQuery.data ?? [], [membersQuery.data])

  // OT-0009 §1.2 #3/#5/#6/#7/#8 — añadir/editar/quitar miembro y
  // transferir liderazgo son la misma pregunta de acceso
  // (`can_manage_team` en backend): admin∪líder. Antes ninguno de los
  // controles de esta vista lo comprobaba.
  const { isMember, isLead } = useProjectAccess(project, members)
  const canManage = canManageTeam(me?.role, me?.tenant?.access, { isMember, isLead })

  // OT-0058 §4.5 — las tres secciones salen de la sección de cada
  // miembro (`project_members.section`), no de su tier: un editor puede
  // participar solo revisando. El tier es el máximo; la sección, lo que
  // hace en ESTE proyecto.
  const operationalMembers = useMemo(
    () => members.filter((m) => m.section === 'operational'),
    [members],
  )
  const extendedMembers = useMemo(
    () => members.filter((m) => m.section === 'extended'),
    [members],
  )
  const stakeholderMembers = useMemo(
    () => members.filter((m) => m.section === 'stakeholder'),
    [members],
  )
  // El diálogo de transferencia de liderazgo solo ofrece a quien puede
  // liderar de verdad (D8): tier que produce contenido.
  const internalMembers = operationalMembers

  if (membersQuery.isLoading) {
    return (
      <ScrollPage padding="tight">
        <p className="font-mono text-xs text-ink-subtle">{t('team.loading')}</p>
      </ScrollPage>
    )
  }
  if (membersQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={membersQuery.error} />
      </ScrollPage>
    )
  }

  return (
    <ScrollPage padding="tight">
      <div className="max-w-7xl">
      <header className="mb-6">
        <h2 className="font-display text-2xl font-semibold text-ink">
          {t('team.header_title')}
        </h2>
        <p className="mt-1 font-body text-sm text-ink-muted max-w-prose">
          {t('team.header_subtitle')}
        </p>
      </header>

      <div className="space-y-8">
        <TeamSection
          kind="operational"
          members={operationalMembers}
          roles={roles}
          projectId={project.id}
          leadMemberId={project.lead_member_id}
          canManage={canManage}
          onAdd={() => setAddOperationalOpen(true)}
          onTransfer={() => setTransferOpen(true)}
          onRemove={setMemberToRemove}
          onEditRoles={setMemberEditingRoles}
        />
        <TeamSection
          kind="extended"
          members={extendedMembers}
          roles={roles}
          projectId={project.id}
          leadMemberId={null}
          canManage={canManage}
          onAdd={() => setAddExtendedOpen(true)}
          onRemove={setMemberToRemove}
          onEditRoles={setMemberEditingRoles}
        />
        <TeamSection
          kind="stakeholder"
          members={stakeholderMembers}
          roles={roles}
          projectId={project.id}
          leadMemberId={null}
          canManage={canManage}
          onAdd={() => setAddStakeholderOpen(true)}
          onRemove={setMemberToRemove}
          onEditRoles={setMemberEditingRoles}
        />
      </div>

      <AddInternalMemberDialog
        open={addOperationalOpen}
        onClose={() => setAddOperationalOpen(false)}
        projectId={project.id}
        tenantId={project.tenant_id}
        existingMembers={members}
        roles={internalRoles}
        mode="operational"
      />
      <AddInternalMemberDialog
        open={addExtendedOpen}
        onClose={() => setAddExtendedOpen(false)}
        projectId={project.id}
        tenantId={project.tenant_id}
        existingMembers={members}
        roles={internalRoles}
        mode="extended"
      />
      <AddStakeholderDialog
        open={addStakeholderOpen}
        onClose={() => setAddStakeholderOpen(false)}
        projectId={project.id}
        roles={stakeholderRoles}
      />
      <TransferOwnershipDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        projectId={project.id}
        internalMembers={internalMembers}
        roles={roles}
        currentLeadMemberId={project.lead_member_id}
      />
      <RemoveMemberDialog
        key={memberToRemove?.id ?? 'none'}
        open={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        projectId={project.id}
        member={memberToRemove}
        members={members}
      />
      {memberEditingRoles ? (
        <EditMemberRolesDialog
          key={memberEditingRoles.id}
          member={memberEditingRoles}
          memberName={memberDisplayName(memberEditingRoles)}
          projectId={project.id}
          roles={
            memberEditingRoles.section === 'stakeholder'
              ? stakeholderRoles
              : internalRoles
          }
          onClose={() => setMemberEditingRoles(null)}
        />
      ) : null}
      </div>
    </ScrollPage>
  )
}

// ─── Section ───────────────────────────────────────────────────────────────
type SectionKind = 'operational' | 'extended' | 'stakeholder'

interface TeamSectionProps {
  kind: SectionKind
  members: ProjectMember[]
  /** Todos los roles del proyecto, para mostrar el nombre de cada rol de
   *  cada miembro (aunque sea de otra sección, p. ej. un dato heredado).
   *  Las opciones para elegir las da el diálogo, filtradas por sección. */
  roles: RoleOption[]
  projectId: string
  /** `project.lead_member_id` — solo aplica a la sección operativa para
   * pintar el badge "Líder activo". `null` en las otras secciones. */
  leadMemberId: string | null
  /** admin∪líder del proyecto (OT-0009) — decide si "Añadir miembro",
   * "Transferir liderazgo" y las acciones de cada fila se ofrecen. */
  canManage: boolean
  onAdd: () => void
  /** Solo en operational: callback del botón "Transferir liderazgo". */
  onTransfer?: () => void
  onRemove: (member: ProjectMember) => void
  onEditRoles: (member: ProjectMember) => void
}

/** i18n keys por sección. Mantener todas como `as const` para que
 * TypeScript reconozca los literales y `t()` infiera el tipo. */
const SECTION_I18N = {
  operational: {
    title: 'team.operational.title' as const,
    subtitle: 'team.operational.subtitle' as const,
    subtitle_zero: 'team.operational.subtitle_zero' as const,
    empty: 'team.operational.empty' as const,
    add: 'team.operational.add' as const,
    section_help: 'team.operational.section_help' as const,
  },
  extended: {
    title: 'team.extended.title' as const,
    subtitle: 'team.extended.subtitle' as const,
    subtitle_zero: 'team.extended.subtitle_zero' as const,
    empty: 'team.extended.empty' as const,
    add: 'team.extended.add' as const,
    section_help: 'team.extended.section_help' as const,
  },
  stakeholder: {
    title: 'team.stakeholders.title' as const,
    subtitle: 'team.stakeholders.subtitle' as const,
    subtitle_zero: 'team.stakeholders.subtitle_zero' as const,
    empty: 'team.stakeholders.empty' as const,
    add: 'team.stakeholders.add' as const,
    section_help: null,
  },
}

function TeamSection({
  kind,
  members,
  roles,
  projectId,
  leadMemberId,
  canManage,
  onAdd,
  onTransfer,
  onRemove,
  onEditRoles,
}: TeamSectionProps) {
  const { t } = useTranslation('projects')
  const keys = SECTION_I18N[kind]
  const subtitle =
    members.length === 0
      ? t(keys.subtitle_zero)
      : t(keys.subtitle, { count: members.length })

  // OT-0009 §1.2 #5/#6 — "gestionar el equipo no es una acción que un
  // miembro raso 'casi' pueda hacer": se oculta, no se deshabilita.
  const showTransferButton =
    kind === 'operational' && typeof onTransfer === 'function' && canManage
  // El botón de transferencia tiene sentido sólo si hay un lead actual
  // o al menos un candidato potencial entre los members. La validación
  // fina vive en el diálogo (filtro por `is_project_lead`); aquí solo
  // habilitamos si hay >= 1 member operativo (el caso edge "no hay
  // members todavía" queda con botón habilitado pero el dialog mostrará
  // su empty state correspondiente).
  const transferEnabled = members.length > 0

  return (
    <section>
      <header className="flex items-end justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-semibold text-ink">
            {t(keys.title)}
          </h3>
          <p className="text-sm text-ink-muted">{subtitle}</p>
          {keys.section_help ? (
            <p className="text-xs text-ink-subtle font-body mt-1 max-w-prose">
              {t(keys.section_help)}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {showTransferButton ? (
            <button
              type="button"
              onClick={onTransfer}
              disabled={!transferEnabled}
              className="inline-flex items-center gap-2 rounded-md border border-line px-3 py-1.5 font-ui text-xs text-ink hover:border-line-strong hover:bg-surface-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowRightLeft className="w-3.5 h-3.5" />
              {t('team.operational.transfer_button')}
            </button>
          ) : null}
          {canManage ? (
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center gap-2 rounded-md border border-line px-3 py-1.5 font-ui text-xs text-ink hover:border-line-strong hover:bg-surface-muted transition-colors"
            >
              <UserPlus className="w-3.5 h-3.5" />
              {t(keys.add)}
            </button>
          ) : null}
        </div>
      </header>

      {members.length === 0 ? (
        <EmptyTeamSection kind={kind} />
      ) : (
        <MembersTable
          members={members}
          roles={roles}
          projectId={projectId}
          leadMemberId={leadMemberId}
          canManage={canManage}
          onRemove={onRemove}
          onEditRoles={onEditRoles}
        />
      )}
    </section>
  )
}

function EmptyTeamSection({ kind }: { kind: SectionKind }) {
  const { t } = useTranslation('projects')
  return (
    <div className="rounded-md border border-dashed border-line bg-surface p-8 text-center">
      <p className="font-body text-sm text-ink-muted max-w-prose mx-auto">
        {t(SECTION_I18N[kind].empty)}
      </p>
    </div>
  )
}

// ─── Members table ──────────────────────────────────────────────────────────
interface MembersTableProps {
  members: ProjectMember[]
  roles: RoleOption[]
  projectId: string
  leadMemberId: string | null
  canManage: boolean
  onRemove: (member: ProjectMember) => void
  onEditRoles: (member: ProjectMember) => void
}

function MembersTable({
  members,
  roles,
  projectId,
  leadMemberId,
  canManage,
  onRemove,
  onEditRoles,
}: MembersTableProps) {
  const { t } = useTranslation('projects')
  return (
    <div className="bg-surface border border-line rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted text-ink-muted text-[11px] uppercase tracking-wider">
          <tr>
            <th className="text-left font-medium px-5 py-3">
              {t('team.table.name')}
            </th>
            <th className="text-left font-medium px-3 py-3">
              {t('team.table.email')}
            </th>
            <th className="text-left font-medium px-3 py-3 w-48">
              {t('team.table.role')}
            </th>
            <th className="text-left font-medium px-3 py-3 w-48">
              {t('team.table.raci')}
            </th>
            <th
              className="text-center font-medium px-3 py-3 w-32"
              title={t('team.table.subscribe_all_help')}
            >
              <span className="inline-flex items-center gap-1.5">
                <Bell className="w-3 h-3" />
                {t('team.table.subscribe_all')}
              </span>
            </th>
            <th className="px-3 py-3 w-12" aria-label="actions" />
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {members.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              roles={roles}
              projectId={projectId}
              isLead={leadMemberId === m.id}
              canManage={canManage}
              onRemove={() => onRemove(m)}
              onEditRoles={() => onEditRoles(m)}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MemberRow({
  member,
  roles,
  projectId,
  isLead,
  canManage,
  onRemove,
  onEditRoles,
}: {
  member: ProjectMember
  roles: RoleOption[]
  projectId: string
  /** Doc 26 §2.3 — true si `member.id === project.lead_member_id`. La
   * fila pinta un badge "Líder activo" + un Crown icon junto al nombre. */
  isLead: boolean
  /** admin∪líder del proyecto (OT-0009) — sin esto, Rol/RACI/notificar
   * quedan de solo lectura y "Quitar" se oculta. */
  canManage: boolean
  onRemove: () => void
  onEditRoles: () => void
}) {
  const { t } = useTranslation('projects')
  const queryClient = useQueryClient()
  const { accessToken } = useAccessToken()

  const updateMutation = useMutation({
    mutationFn: async (patch: {
      raci_role?: RaciRole
      subscribe_all_events?: boolean
      section?: TeamSection
    }) => {
      if (!accessToken) throw new Error('no_access_token')
      return updateProjectMember(accessToken, projectId, member.id, patch)
    },
    onSuccess: () => {
      pushToast({ kind: 'success', message: t('team.toasts.updated') })
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'members'],
      })
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('team.toasts.update_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : fallback
      pushToast({ kind: 'critical', message, duration: 6000 })
    },
  })

  // OT-0058 §4.5 — mover entre operativo y extendido, si su licencia
  // admite la otra sección. Salir del operativo siendo líder o responsable
  // de documentos lo rechaza el backend (409) con la causa.
  const otherSection: TeamSection | null =
    member.section === 'operational'
      ? 'extended'
      : member.section === 'extended'
        ? 'operational'
        : null
  const canMove =
    canManage &&
    otherSection !== null &&
    canJoinSection(member.user_tier, otherSection)

  const displayName = memberDisplayName(member)
  const emailLine =
    member.user_email && member.user_display_name
      ? member.user_email
      : member.external_email ?? '—'

  const initials = buildInitials(
    member.user_display_name ??
      member.user_email ??
      member.external_email ??
      '?',
  )

  return (
    <tr className="hover:bg-surface-muted group">
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-brand-soft text-brand grid place-items-center text-xs font-semibold shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex items-center gap-2">
            <p className="font-medium text-ink truncate">{displayName}</p>
            {isLead ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-brand-soft text-brand px-2 py-0.5 text-[10px] font-medium font-ui uppercase tracking-wider shrink-0"
                title={t('team.operational.lead_badge')}
              >
                <Crown className="w-3 h-3" />
                {t('team.operational.lead_badge')}
              </span>
            ) : null}
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-ink-muted text-xs truncate">{emailLine}</td>
      <td className="px-3 py-3">
        {/* OT-0058 — una persona puede tener varios roles en el proyecto.
            La celda los muestra; se editan en su diálogo. */}
        <div className="flex items-start gap-1.5">
          <ul className="flex flex-wrap gap-1 min-w-0">
            {member.roles.map((key) => (
              <li
                key={key}
                className="rounded bg-surface-muted px-1.5 py-0.5 text-xs text-ink"
              >
                {roleLabel(key, roles)}
              </li>
            ))}
          </ul>
          {canManage ? (
            <button
              type="button"
              onClick={onEditRoles}
              aria-label={t('team.roles.edit_aria', { name: displayName })}
              title={t('team.roles.edit')}
              className="p-1 rounded-md text-ink-subtle hover:text-ink hover:bg-surface-muted shrink-0"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-3">
        <select
          value={member.raci_role ?? ''}
          onChange={(e) =>
            updateMutation.mutate({
              raci_role: (e.target.value || null) as RaciRole,
            })
          }
          disabled={updateMutation.isPending || !canManage}
          className="bg-canvas border border-line rounded-md px-2.5 py-1.5 text-xs w-full"
        >
          <option value="">{t('team.raci_options.none')}</option>
          <option value="R">{t('team.raci_options.R')}</option>
          <option value="A">{t('team.raci_options.A')}</option>
          <option value="C">{t('team.raci_options.C')}</option>
          <option value="I">{t('team.raci_options.I')}</option>
        </select>
      </td>
      <td className="px-3 py-3 text-center">
        <label
          className="inline-flex items-center justify-center cursor-pointer"
          title={t('team.table.subscribe_all_help')}
        >
          <input
            type="checkbox"
            checked={member.subscribe_all_events}
            onChange={(e) =>
              updateMutation.mutate({ subscribe_all_events: e.target.checked })
            }
            disabled={updateMutation.isPending || !canManage}
            className="w-4 h-4 rounded border-line text-brand focus:ring-brand focus:ring-offset-0"
            aria-label={t('team.table.subscribe_all_aria', {
              name: displayName,
            })}
          />
        </label>
      </td>
      <td className="px-3 py-3 text-right whitespace-nowrap">
        {canMove && otherSection ? (
          <button
            type="button"
            onClick={() => updateMutation.mutate({ section: otherSection })}
            disabled={updateMutation.isPending}
            aria-label={t(`team.row_actions.move_to_${otherSection}_aria`, {
              name: displayName,
            })}
            title={t(`team.row_actions.move_to_${otherSection}`)}
            className="p-1.5 rounded-md text-ink-subtle hover:text-ink hover:bg-surface-muted opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity disabled:opacity-50"
          >
            <ArrowDownUp className="w-4 h-4" />
          </button>
        ) : null}
        {canManage ? (
          <button
            type="button"
            onClick={onRemove}
            aria-label={t('team.row_actions.remove_aria', { name: displayName })}
            title={t('team.row_actions.remove')}
            className="p-1.5 rounded-md text-ink-subtle hover:text-critical hover:bg-critical-soft opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        ) : null}
      </td>
    </tr>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function memberDisplayName(member: ProjectMember): string {
  return (
    member.user_display_name ?? member.user_email ?? member.external_email ?? '—'
  )
}

function buildInitials(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return '··'
  const at = trimmed.indexOf('@')
  const head = at > 0 ? trimmed.slice(0, at) : trimmed
  const parts = head.split(/[\s.\-_]+/).filter(Boolean)
  if (parts.length === 0) return '··'
  if (parts.length === 1) {
    const word = parts[0] ?? ''
    return word.slice(0, 2).toUpperCase()
  }
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase()
}

function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })}
    </div>
  )
}
